/* ==========================================================================
   Landing page (split-test PDP on a page template)

   1. <fs-daily-countdown> — a 24 hour countdown that resets every day at a
      configurable hour in the visitor's own timezone.
   2. Price switching — on a page template Liquid cannot read ?variant=, so the
      buy box renders one price block per variant and this swaps them on
      variant change. No network round trip, and all money formatting stays in
      Liquid.
   3. In-page CTA scrolling — every CTA points at the buy box rather than
      another URL.

   Nothing here touches checkout: the 30% comes from a Shopify automatic
   discount, which needs no code and no redirect.
   ========================================================================== */

function fsPadTime(value) {
  return value < 10 ? '0' + value : String(value);
}

class FsDailyCountdown extends HTMLElement {
  connectedCallback() {
    this.resetHour = parseInt(this.dataset.resetHour, 10);
    if (isNaN(this.resetHour) || this.resetHour < 0 || this.resetHour > 23) this.resetHour = 0;

    this.hoursEl = this.querySelector('[data-countdown-hours]');
    this.minutesEl = this.querySelector('[data-countdown-minutes]');
    this.secondsEl = this.querySelector('[data-countdown-seconds]');
    this.srEl = this.querySelector('[data-countdown-sr]');

    this.tick();
    this.timer = setInterval(this.tick.bind(this), 1000);
  }

  disconnectedCallback() {
    if (this.timer) clearInterval(this.timer);
  }

  /**
   * Milliseconds until the next reset. The target is today at the reset hour;
   * once that moment has passed it rolls to tomorrow, so the clock always sits
   * between 24:00:00 and 00:00:00 and starts over each day.
   */
  remaining() {
    const now = new Date();
    const target = new Date(now.getFullYear(), now.getMonth(), now.getDate(), this.resetHour, 0, 0, 0);
    if (target.getTime() <= now.getTime()) target.setDate(target.getDate() + 1);
    return target.getTime() - now.getTime();
  }

  tick() {
    const totalSeconds = Math.max(0, Math.floor(this.remaining() / 1000));
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    if (this.hoursEl) this.hoursEl.textContent = fsPadTime(hours);
    if (this.minutesEl) this.minutesEl.textContent = fsPadTime(minutes);
    if (this.secondsEl) this.secondsEl.textContent = fsPadTime(seconds);

    // Announce once a minute rather than once a second.
    if (this.srEl && seconds === 0) {
      this.srEl.textContent = hours + ' hours ' + minutes + ' minutes remaining';
    }

    this.setAttribute('data-countdown-ready', 'true');
  }
}

if (!customElements.get('fs-daily-countdown')) {
  customElements.define('fs-daily-countdown', FsDailyCountdown);
}

/* --------------------------------------------------------------------------
   Per-variant price blocks
   -------------------------------------------------------------------------- */
document.addEventListener('variant:change', (event) => {
  const variant = event.detail && event.detail.variant;
  if (!variant) return;

  const scope = event.target.closest('product-section') || document;
  const blocks = scope.querySelectorAll('[data-fs-price-variant]');
  if (!blocks.length) return;

  blocks.forEach((block) => {
    block.hidden = block.dataset.fsPriceVariant !== String(variant.id);
  });
});

/* --------------------------------------------------------------------------
   In-page CTA scrolling

   Every CTA on the landing page points at #fs-landing-buybox rather than
   another URL. Delegated from the document so it keeps working no matter which
   landing sections are present, and it offsets by the sticky header height so
   the buy box is not hidden underneath it.
   -------------------------------------------------------------------------- */
document.addEventListener('click', (event) => {
  const target = event.target instanceof Element ? event.target : null;
  if (!target) return;

  const link = target.closest('[data-fs-scroll]');
  if (!link) return;

  const href = link.getAttribute('href') || '';
  if (!href.startsWith('#')) return;

  const destination = document.querySelector(href);
  if (!destination) return;

  event.preventDefault();

  const header = document.querySelector('.outer-header-wrapper');
  const offset = header ? header.offsetHeight : 0;

  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    window.scrollTo(0, destination.getBoundingClientRect().top + window.pageYOffset - offset);
    return;
  }

  const startY = window.pageYOffset;
  const targetY = destination.getBoundingClientRect().top + startY - offset;
  const duration = 700;
  let startTime = null;

  const ease = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

  const step = (timestamp) => {
    if (startTime === null) startTime = timestamp;
    const progress = Math.min((timestamp - startTime) / duration, 1);
    window.scrollTo(0, startY + (targetY - startY) * ease(progress));
    if (progress < 1) window.requestAnimationFrame(step);
  };

  window.requestAnimationFrame(step);
});
